/**
 * SVG canvas visualization for wire composition
 */
import React, { useMemo, useCallback } from "react";
import { getMockCreditsBalance } from "../../../../../../mocks";
import { ASSET_PATHS } from "../../../../../../config";
import { CANVAS_LAYOUT, CANVAS_GRID_STYLE, SignalNodeCircle, AssetListItem } from "../../canvas";
import type { SmartAssetData, CompositionLink } from "../../types";
import type { CoreLinkDetail, SignalNode, WireEdge, CorePort, ActivityItem } from "./types";

export interface WireCanvasProps {
  selectedAssets: SmartAssetData[];
  links: CompositionLink[];
  selectedLinkId: string | null;
  coreAsset?: SmartAssetData;
  signalConnections: CoreLinkDetail[];
  valueInAssets: SmartAssetData[];
  valueOutAssets: SmartAssetData[];
  hasSignal: boolean;
  hasValueIn: boolean;
  hasValueOut: boolean;
  hasCharge: boolean;
  activityItems: ActivityItem[];
  getAssetName: (id: string) => string;
}

export const WireCanvas: React.FC<WireCanvasProps> = ({
  selectedAssets,
  links,
  selectedLinkId,
  coreAsset,
  signalConnections,
  valueInAssets,
  valueOutAssets,
  hasSignal,
  hasValueIn,
  hasValueOut,
  hasCharge,
  activityItems,
  getAssetName,
}) => {
  const creditsBalance = useMemo(() => getMockCreditsBalance(), []);

  const coreCoords = useMemo(() => {
    const { core } = CANVAS_LAYOUT;
    const center = { x: core.x + core.w / 2, y: core.y + core.h / 2 };
    return {
      top: { x: center.x, y: core.y },
      left: { x: core.x, y: center.y },
      right: { x: core.x + core.w, y: center.y },
      bottomLeft: { x: core.x + core.w * 0.35, y: core.y + core.h },
      bottomRight: { x: core.x + core.w * 0.65, y: core.y + core.h },
    };
  }, []);

  const signalNodes = useMemo<SignalNode[]>(() => {
    const count = signalConnections.length;
    const { signalArc } = CANVAS_LAYOUT;
    return signalConnections.map((item, index) => {
      const t = count > 1 ? index / (count - 1) : 0.5;
      const x = signalArc.startX + t * (signalArc.endX - signalArc.startX);
      const arcOffset = Math.sin(Math.PI * t) * signalArc.amplitude;
      const y = signalArc.baseY - arcOffset + (index % 2 === 0 ? -1.2 : 1.2);
      return {
        id: item.link.id,
        x,
        y,
        asset: item.externalAsset,
        direction: item.direction,
        label: `${getAssetName(item.externalId)} ${item.coreInterface}`,
      };
    });
  }, [signalConnections, getAssetName]);

  const curvePath = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }) => {
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      if (Math.abs(dx) < Math.abs(dy)) {
        const curve = Math.max(4, Math.min(10, Math.abs(dy) * 0.35));
        return `M ${start.x} ${start.y} C ${start.x} ${start.y + curve}, ${end.x} ${
          end.y - curve
        }, ${end.x} ${end.y}`;
      }
      const curve = Math.max(4, Math.min(10, Math.abs(dx) * 0.35));
      return `M ${start.x} ${start.y} C ${start.x + curve} ${start.y}, ${end.x - curve} ${
        end.y
      }, ${end.x} ${end.y}`;
    },
    [],
  );

  const wires = useMemo<WireEdge[]>(() => {
    const items: WireEdge[] = [];
    const { leftBox, rightBox, payment, gateLeft, gateRight } = CANVAS_LAYOUT;

    signalNodes.forEach((node) => {
      const start = node.direction === "in" ? { x: node.x, y: node.y } : coreCoords.top;
      const end = node.direction === "in" ? coreCoords.top : { x: node.x, y: node.y };
      items.push({
        id: `signal-${node.id}`,
        path: curvePath(start, end),
        color: "#22d3ee",
        markerEnd: node.direction === "in" ? "url(#marker-signal)" : undefined,
        markerStart: node.direction === "out" ? "url(#marker-signal)" : undefined,
        label: node.label,
      });
    });

    if (hasValueIn) {
      const start = { x: leftBox.x + leftBox.w, y: leftBox.y + leftBox.h / 2 };
      const end = coreCoords.left;
      items.push({
        id: "value-in",
        path: curvePath(start, end),
        color: "#22c55e",
        markerEnd: "url(#marker-value)",
        label: "Value inflow",
      });
    }

    if (hasValueOut) {
      const start = coreCoords.right;
      const end = { x: rightBox.x, y: rightBox.y + rightBox.h / 2 };
      items.push({
        id: "value-out",
        path: curvePath(start, end),
        color: "#f59e0b",
        markerEnd: "url(#marker-value)",
        label: "Value outflow",
      });
    }

    if (hasCharge) {
      const start = coreCoords.bottomLeft;
      const end = { x: payment.x + payment.w, y: payment.y + payment.h / 2 };
      items.push({
        id: "charge",
        path: curvePath(start, end),
        color: "#fb923c",
        markerEnd: "url(#marker-fee)",
        label: "Oracle fee",
      });
    }

    if (hasValueOut) {
      const leftGateStart = coreCoords.bottomLeft;
      const leftGateEnd = { x: gateLeft.x + gateLeft.w / 2, y: gateLeft.y };
      items.push({
        id: "gate-left",
        path: curvePath(leftGateStart, leftGateEnd),
        color: "#38bdf8",
        markerEnd: "url(#marker-signal)",
        label: "Logic module gate",
      });

      const rightGateStart = coreCoords.bottomRight;
      const rightGateEnd = { x: gateRight.x + gateRight.w / 2, y: gateRight.y };
      items.push({
        id: "gate-right",
        path: curvePath(rightGateStart, rightGateEnd),
        color: "#38bdf8",
        markerEnd: "url(#marker-signal)",
        label: "Disbursement gate",
      });
    }

    return items;
  }, [signalNodes, hasValueIn, hasValueOut, hasCharge, coreCoords, curvePath]);

  const corePorts = useMemo<CorePort[]>(() => {
    const ports = [
      hasSignal && { id: "signal", x: coreCoords.top.x, y: coreCoords.top.y, color: "#22d3ee" },
      hasValueIn && {
        id: "value-in",
        x: coreCoords.left.x,
        y: coreCoords.left.y,
        color: "#22c55e",
      },
      hasValueOut && {
        id: "value-out",
        x: coreCoords.right.x,
        y: coreCoords.right.y,
        color: "#f59e0b",
      },
      hasCharge && {
        id: "charge",
        x: coreCoords.bottomLeft.x,
        y: coreCoords.bottomLeft.y,
        color: "#fb923c",
      },
      hasValueOut && {
        id: "gate",
        x: coreCoords.bottomRight.x,
        y: coreCoords.bottomRight.y,
        color: "#f59e0b",
      },
    ].filter(Boolean) as CorePort[];
    return ports;
  }, [hasSignal, hasValueIn, hasValueOut, hasCharge, coreCoords]);

  const selectedLink = selectedLinkId
    ? links.find((link) => link.id === selectedLinkId)
    : undefined;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-sa-text-muted">
        <span>Composition Canvas</span>
        <span className="text-[11px] text-sa-text-dim">View Only</span>
      </div>

      <div className="mt-3 grid gap-4 lg:grid-cols-[1fr_260px]">
        <div
          className="relative min-h-[360px] rounded-2xl border border-sa-border/70 shadow-inner overflow-hidden"
          style={CANVAS_GRID_STYLE}
        >
          <div className="absolute left-4 top-3 text-[11px] text-sa-text">Composition Canvas</div>
          <div className="absolute right-4 top-3 text-[11px] uppercase tracking-wide text-sa-text-muted">
            View Only
          </div>

          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <marker
                id="marker-signal"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill="#22d3ee" />
              </marker>
              <marker
                id="marker-value"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill="#f59e0b" />
              </marker>
              <marker
                id="marker-fee"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill="#fb923c" />
              </marker>
            </defs>

            {wires.map((edge) => (
              <path
                key={edge.id}
                d={edge.path}
                stroke={edge.color}
                strokeWidth={1.6}
                fill="none"
                markerEnd={edge.markerEnd}
                markerStart={edge.markerStart}
                strokeLinecap="round"
                opacity={0.75}
              >
                <title>{edge.label}</title>
              </path>
            ))}
          </svg>

          {hasSignal && (
            <div className="absolute left-1/2 top-6 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] text-sa-text-dim">
              External Data Feeds
            </div>
          )}

          {signalNodes.map((node) => (
            <SignalNodeCircle
              key={node.id}
              id={node.id}
              x={node.x}
              y={node.y}
              label={node.label}
              iconPath={node.asset?.iconPath}
              name={node.asset?.name}
              ticker={node.asset?.ticker}
            />
          ))}

          {hasValueIn && (
            <div
              className="absolute rounded-lg border border-sa-border/70 bg-sa-surface/80 px-3 py-2 text-[10px] text-sa-text shadow-lg"
              style={{
                left: `${CANVAS_LAYOUT.leftBox.x}%`,
                top: `${CANVAS_LAYOUT.leftBox.y}%`,
                width: `${CANVAS_LAYOUT.leftBox.w}%`,
                height: `${CANVAS_LAYOUT.leftBox.h}%`,
              }}
            >
              <div className="text-[9px] uppercase tracking-wider text-sa-text-dim">
                External Funds
              </div>
              <div className="mt-1 space-y-1">
                {valueInAssets.map((asset) => (
                  <AssetListItem
                    key={asset.id}
                    id={asset.id}
                    name={asset.name}
                    iconPath={asset.iconPath}
                  />
                ))}
              </div>
            </div>
          )}

          {hasValueOut && (
            <div
              className="absolute rounded-lg border border-sa-border/70 bg-sa-surface/80 px-3 py-2 text-[10px] text-sa-text shadow-lg"
              style={{
                left: `${CANVAS_LAYOUT.rightBox.x}%`,
                top: `${CANVAS_LAYOUT.rightBox.y}%`,
                width: `${CANVAS_LAYOUT.rightBox.w}%`,
                height: `${CANVAS_LAYOUT.rightBox.h}%`,
              }}
            >
              <div className="text-[9px] uppercase tracking-wider text-sa-text-dim">
                External Accounts
              </div>
              <div className="mt-1 space-y-1">
                {valueOutAssets.map((asset) => (
                  <AssetListItem
                    key={asset.id}
                    id={asset.id}
                    name={asset.name}
                    iconPath={asset.iconPath}
                  />
                ))}
              </div>
            </div>
          )}

          <div
            className="absolute rounded-2xl border border-cyan-400/70 bg-sa-surface/90 px-4 py-3 text-center text-sa-text shadow-[0_0_25px_rgba(34,211,238,0.2)]"
            style={{
              left: `${CANVAS_LAYOUT.core.x}%`,
              top: `${CANVAS_LAYOUT.core.y}%`,
              width: `${CANVAS_LAYOUT.core.w}%`,
              height: `${CANVAS_LAYOUT.core.h}%`,
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400/70">
              Smart Asset Core
            </div>
            <div className="mt-2 flex items-center justify-center gap-3">
              {coreAsset?.iconPath ? (
                <img
                  src={coreAsset.iconPath}
                  alt={coreAsset.name}
                  className="h-10 w-10 rounded-lg object-cover"
                />
              ) : (
                <div className="h-10 w-10 rounded-lg border border-cyan-500/40 bg-sa-surface/80 flex items-center justify-center text-xs text-cyan-400">
                  CORE
                </div>
              )}
              <div className="text-left">
                <div className="text-base font-semibold">{coreAsset?.name ?? "Select Asset"}</div>
                <div className="text-[10px] text-sa-text-dim">{coreAsset?.ticker ?? "CORE"}</div>
              </div>
            </div>
          </div>

          {corePorts.map((port) => (
            <span
              key={port.id}
              className="absolute h-2.5 w-2.5 rounded-full border border-white/60 shadow-sm"
              style={{
                left: `${port.x}%`,
                top: `${port.y}%`,
                transform: "translate(-50%, -50%)",
                backgroundColor: port.color,
              }}
            />
          ))}

          {hasCharge && (
            <div
              className="absolute rounded-lg border border-orange-400/60 bg-sa-surface/90 px-3 py-2 text-[10px] text-orange-400 shadow-lg"
              style={{
                left: `${CANVAS_LAYOUT.payment.x}%`,
                top: `${CANVAS_LAYOUT.payment.y}%`,
                width: `${CANVAS_LAYOUT.payment.w}%`,
                height: `${CANVAS_LAYOUT.payment.h}%`,
              }}
            >
              <div className="text-[9px] uppercase tracking-wider text-orange-400">
                Payment Buffers
              </div>
              <div className="mt-2 space-y-1 text-[10px] text-orange-400">
                <div className="flex items-center justify-between">
                  <span>SA Credits</span>
                  <span className="flex items-center gap-1">
                    {creditsBalance.balance.toFixed(1)}
                    <img src={ASSET_PATHS.PAIR_ICON} alt="PAIR" className="h-3 w-3" />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>PAIR</span>
                  <span className="flex items-center gap-1">
                    {creditsBalance.balance.toFixed(1)}
                    <img src={ASSET_PATHS.PAIR_ICON} alt="PAIR" className="h-3 w-3" />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Charge/Battery</span>
                  <span>{creditsBalance.burnRate.toFixed(1)}</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-orange-500/10">
                  <div className="h-1.5 w-2/3 rounded-full bg-orange-400/80" />
                </div>
              </div>
            </div>
          )}

          {hasValueOut && (
            <>
              <div
                className="absolute rounded-lg border border-cyan-500/50 bg-sa-surface/90 px-3 py-2 text-[10px] text-cyan-400"
                style={{
                  left: `${CANVAS_LAYOUT.gateLeft.x}%`,
                  top: `${CANVAS_LAYOUT.gateLeft.y}%`,
                  width: `${CANVAS_LAYOUT.gateLeft.w}%`,
                  height: `${CANVAS_LAYOUT.gateLeft.h}%`,
                }}
              >
                <div className="text-[9px] uppercase tracking-wide text-cyan-400">Logic Module</div>
                <div className="text-[9px] text-cyan-400/80">Multi-sig</div>
              </div>
              <div
                className="absolute rounded-lg border border-cyan-500/50 bg-sa-surface/90 px-3 py-2 text-[10px] text-cyan-400"
                style={{
                  left: `${CANVAS_LAYOUT.gateRight.x}%`,
                  top: `${CANVAS_LAYOUT.gateRight.y}%`,
                  width: `${CANVAS_LAYOUT.gateRight.w}%`,
                  height: `${CANVAS_LAYOUT.gateRight.h}%`,
                }}
              >
                <div className="text-[9px] uppercase tracking-wide text-cyan-400">Disbursement</div>
                <div className="text-[9px] text-cyan-400/80">Whitelist</div>
              </div>
            </>
          )}

          {selectedAssets.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-sa-text-dim">
              Select assets to visualize wiring.
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-sa-border/70 bg-sa-surface/80 p-3 text-xs text-sa-text">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-sa-text">
              Legend
            </div>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-6 rounded bg-cyan-400/80" />
                <span>Signal/Oracle</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-6 rounded bg-sa-success/80" />
                <span>Value In</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-6 rounded bg-sa-warning/80" />
                <span>Value Out</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-6 rounded bg-orange-400/80" />
                <span>Oracle Fee</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sa-warning">↔</span>
                <span>Bidirectional Value</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-sa-border/70 bg-sa-surface/80 p-4 text-xs text-sa-text">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-sa-text">
              Properties
            </div>
            {selectedLink ? (
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-[10px] text-sa-text-dim">Selected</div>
                  <div className="text-sa-text">
                    {getAssetName(selectedLink.source_id)} → {getAssetName(selectedLink.target_id)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-sa-text-dim">Interfaces</div>
                  <div className="text-sa-text">
                    {selectedLink.source_interface} → {selectedLink.target_interface}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sa-text">Rules</span>
                  <input type="checkbox" disabled className="h-4 w-4 accent-purple-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sa-text">Gate Required</span>
                  <input type="checkbox" disabled className="h-4 w-4 accent-purple-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sa-text">Link Type</span>
                  <span className="text-sa-text">{selectedLink.link_type}</span>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sa-text-dim">Select a connection to view properties.</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-sa-border/70 bg-sa-surface/80 px-4 py-3 text-[11px] text-sa-text">
        <div className="text-[10px] uppercase tracking-wide text-sa-text-dim">Activity Strip</div>
        {activityItems.length > 0 ? (
          <div className="mt-2 space-y-1">
            {activityItems.map((item) => (
              <div key={item.id} className="text-sa-text">
                [{item.time}] {item.message}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 text-sa-text-muted">No composition activity yet.</div>
        )}
      </div>
    </div>
  );
};
